<template>
  <div class="alignment-dropdown" ref="dropdown">
    <button 
      class="alignment-bbl-dropdown-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen }"
      title="Text Alignment"
    >
      <component :is="currentIcon" />
      <svg class="dropdown-icon" :class="{ 'rotate': isOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    
    <transition name="dropdown-fade">
      <div v-if="isOpen" class="alignment-bbl-dropdown-menu">
        <div class="bbl-dropdown-menu-inner">
          <button
            v-for="option in options"
            :key="option.value"
            class="alignment-option"
            :class="{ 'is-active': currentValue === option.value }"
            @click="selectOption(option)"
          >
            <component :is="option.icon" class="option-icon" />
            <span class="option-label">{{ option.label }}</span>
            <svg v-if="currentValue === option.value" class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { LeftAlign, CenterAlign, RightAlign, JustifyAlign } from '../svg'

export default {
  name: 'AlignmentDropdown',
  components: {
    LeftAlign,
    CenterAlign,
    RightAlign,
    JustifyAlign
  },
  props: {
    modelValue: {
      type: String,
      default: 'left'
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      options: [
        { value: 'left', label: 'Align Left', icon: 'LeftAlign' },
        { value: 'center', label: 'Align Center', icon: 'CenterAlign' },
        { value: 'right', label: 'Align Right', icon: 'RightAlign' },
        { value: 'justify', label: 'Justify', icon: 'JustifyAlign' }
      ]
    }
  },
  computed: {
    currentValue() {
      return this.modelValue || 'left'
    },
    currentIcon() {
      const option = this.options.find(opt => opt.value === this.currentValue)
      return option ? option.icon : 'LeftAlign'
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value)
      this.$emit('change', { target: { value: option.value } })
      this.isOpen = false
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.alignment-dropdown {
  position: relative;
  display: inline-block;
}

.alignment-bbl-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  padding: 0px 4px;
  height: 32px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.alignment-bbl-dropdown-trigger:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.alignment-bbl-dropdown-trigger.is-open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: white;
}

.dropdown-icon {
  color: #6b7280;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.alignment-bbl-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 180px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  overflow: hidden;
}

.bbl-dropdown-menu-inner {
  padding: 6px;
}

.alignment-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  color: #374151;
  font-weight: 400;
}

.alignment-option:hover {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.alignment-option.is-active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  font-weight: 500;
}

.option-icon {
  flex-shrink: 0;
  color: #6b7280;
}

.alignment-option.is-active .option-icon {
  color: #1e40af;
}

.option-label {
  flex: 1;
  font-size: 14px;
}

.check-icon {
  color: #3b82f6;
  flex-shrink: 0;
}

/* Dropdown animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
